import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const benefitIcons = [
  'ri-sparkling-2-line',
  'ri-broadcast-line',
  'ri-user-heart-line',
  'ri-tools-line',
  'ri-hand-coin-line',
];

export default function BenefitsSection() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="benefits" ref={ref} className="bg-brand-white py-24 md:py-32 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-gold" />
            <span className="font-body text-xs text-gold tracking-widest uppercase font-semibold">{t('benefits.badge')}</span>
            <div className="w-8 h-px bg-gold" />
          </div>
          <h2 className="font-heading font-black text-3xl md:text-5xl text-brand-black leading-tight">
            {t('benefits.title1')}
            <br />
            <span className="text-gold">{t('benefits.title2')}</span>
          </h2>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefitIcons.map((icon, i) => (
            <div
              key={i}
              className={`group relative bg-brand-black/[0.03] border border-brand-black/[0.07] rounded-2xl p-8 hover:border-gold/40 transition-all duration-500 cursor-default ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}
            >
              <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5 group-hover:bg-gold/20 transition-colors duration-300">
                <i className={`${icon} text-2xl text-gold`} />
              </div>
              <h3 className="font-heading font-bold text-base text-brand-black mb-3 leading-snug">
                {t(`benefits.${i}.title`)}
              </h3>
              <p className="font-body text-sm text-brand-black/60 leading-relaxed">
                {t(`benefits.${i}.desc`)}
              </p>
            </div>
          ))}

          {/* CTA Card */}
          <div
            className={`relative bg-brand-black rounded-2xl p-8 flex flex-col justify-between overflow-hidden transition-all duration-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: visible ? '500ms' : '0ms' }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.15),transparent_60%)]" />
            <div className="relative z-10">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/20 rounded-xl mb-5">
                <i className="ri-star-line text-2xl text-gold" />
              </div>
              <h3 className="font-heading font-bold text-base text-brand-white mb-3">
                {t('benefits.cta.title')}
              </h3>
              <p className="font-body text-sm text-brand-white/60 leading-relaxed mb-6">
                {t('benefits.cta.desc')}
              </p>
            </div>
            <button
              onClick={() => {
                const el = document.getElementById('contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="relative z-10 font-heading font-bold text-sm bg-gold text-brand-black px-6 py-3 rounded-full hover:bg-gold-light transition-colors duration-300 cursor-pointer whitespace-nowrap text-center"
            >
              {t('benefits.cta.button')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
